export default class UrlUtil {
	private static instances: WeakMap<Window, UrlUtil>;
	private static readonly protocolAndSlashesRegExp = new RegExp('^(?:[a-z]+:)?//', 'i');

	private baseUrl: string | undefined;

	public static getInstance(win: Window = window): UrlUtil {
		if (UrlUtil.instances === undefined) {
			UrlUtil.instances = new WeakMap<Window, UrlUtil>();
			const urlUtil = new UrlUtil(win);
			UrlUtil.instances.set(win, urlUtil);
			return urlUtil;
		} else {
			const res = UrlUtil.instances.get(win);
			if (res !== undefined) {
				return res;
			} else {
				const urlUtil = new UrlUtil(win);
				UrlUtil.instances.set(win, urlUtil);
				return urlUtil;
			}
		}
	}

	private constructor(private readonly win: Window) {
	}

	// TODO (Michael): put this and urlToString() to use. The urlToString() will reduce some repetition and make more readable.
	public toUrlObj(url: string): URL {
		if (!UrlUtil.isAbsoluteUrl(url)) {
			url = this.applyBaseUrl(url);
		}
		return new URL(url);
	}
	
	public toUrlString(urlObj: URL, newParams?: URLSearchParams): string {
		if (newParams !== undefined && newParams !== null) {
			return urlObj.origin + urlObj.pathname +  "?" + newParams.toString() + urlObj.hash;
		}
		return urlObj.origin + urlObj.pathname + urlObj.hash;
	}

	/**
	 * Returns the base url, and ensures it ends with "/"
	 */
	public getBaseUrl(): string {
		if (this.baseUrl === undefined) {
			let baseUrl;
			if (this.win.document.baseURI !== undefined) {
				baseUrl = this.win.document.baseURI;
			} else {
				// Need for older browsers (e.g. IE11)
				const bases = this.win.document.getElementsByTagName("base");
				if (bases.length > 0) {
					baseUrl = bases[bases.length - 1]!.href;
				} else {
					baseUrl = this.win.location.href.substring(0, window.location.href.lastIndexOf("/") + 1);
				}
			}

			this.baseUrl = baseUrl.charAt(baseUrl.length - 1) === "/" ? baseUrl : baseUrl + "/";
		}
		return this.baseUrl;
	}

	/**
	 * Same as the static `applyBaseURL(...)` method but uses
	 * the base URL of this instance.
	 */
	public applyBaseUrl(url: string): string {
		return UrlUtil.applyBaseUrl(this.getBaseUrl(), url);
	}


	/**
	 * Determines if the URL is absolute or not.
	 * Note: This does will return true if the URL is missing the protocol and starts with "//".
	 */
	public static isAbsoluteUrl(url: string): boolean {
		return UrlUtil.protocolAndSlashesRegExp.test(url);
	}


	/**
	 * Applies the base URL to the URL, if the URL is not absolute.
	 * If the URL is missing the protocol, the protocol of the base URL will be applied.
	 */
	public static applyBaseUrl(baseUrl: string, url: string): string {
		const protocolAndSlashes = UrlUtil.protocolAndSlashesRegExp.exec(url);

		if (protocolAndSlashes === null || protocolAndSlashes.length === 0) {
			// URL is relative, apply base URL
			return baseUrl + url;
		} else if (protocolAndSlashes[0]!.startsWith("//")) {
			// URL is missing the protocol
			const baseUrlProtocolAndSlashes = UrlUtil.protocolAndSlashesRegExp.exec(baseUrl)?.[0];
			if (baseUrlProtocolAndSlashes === undefined) {
				console.error("Unable to determine the proper protocol from", baseUrl, url);
				return url;
			} else {
				const baseProtocol = baseUrlProtocolAndSlashes.substring(0, baseUrlProtocolAndSlashes.length - 2);
				return baseProtocol + url;
			}
		}

		// Original URL was already absolute
		return url;
	}
}
